import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

import { ItemTypeEnum } from '../generated-mesh/graphql';

type DownvoteState = {
  [key in ItemTypeEnum]: boolean;
};

interface DownvoteContextValue {
  // Check if any item of a type has been downvoted
  hasDownvoted: (itemType: ItemTypeEnum) => boolean;

  // Mark that an item of this type has been downvoted
  markAsDownvoted: (itemType: ItemTypeEnum) => void;
}

const DownvoteContext = createContext<DownvoteContextValue | undefined>(
  undefined
);

export const DownvoteProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [downvoteState, setDownvoteState] = useState<DownvoteState>({
    [ItemTypeEnum.JOB]: false,
    [ItemTypeEnum.COURSE]: false,
    [ItemTypeEnum.CAREER]: false,
  });

  const hasDownvoted = useCallback(
    (itemType: ItemTypeEnum): boolean => {
      return downvoteState[itemType] || false;
    },
    [downvoteState]
  );

  const markAsDownvoted = useCallback((itemType: ItemTypeEnum) => {
    setDownvoteState((prev) => ({
      ...prev,
      [itemType]: true,
    }));
  }, []);

  const value: DownvoteContextValue = useMemo(
    () => ({
      hasDownvoted,
      markAsDownvoted,
    }),
    [hasDownvoted, markAsDownvoted]
  );

  return (
    <DownvoteContext.Provider value={value}>
      {children}
    </DownvoteContext.Provider>
  );
};

export const useDownvote = (): DownvoteContextValue => {
  const context = useContext(DownvoteContext);
  if (!context) {
    throw new Error('useDownvote must be used within a DownvoteProvider');
  }
  return context;
};
